import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { MasterDocumentPrintModal } from './MasterDocumentPrintModal';
import { useMasterDocumentTranslation } from '../i18n';
import type {
  MasterDocumentPrintApi,
  MasterDocumentPrintConfig,
  PrintContext,
  PrintViewerProps,
} from './MasterDocumentPrinting.types';

// ════════════════════════════════════════════════════════════
// CONTEXTO — null = impresión no configurada en el provider.
// ════════════════════════════════════════════════════════════

const PrintApiContext = createContext<MasterDocumentPrintApi | null>(null);

/**
 * Devuelve la API de impresión expuesta por el `MasterDocumentProvider`, o `null`
 * si no se pasó la prop `printing` (impresión no disponible → retrocompatibilidad).
 * Lo consumen `DocumentList` (vía `MasterDocumentPage`) y `SmartMasterDocument`.
 */
export function useMasterDocumentPrint(): MasterDocumentPrintApi | null {
  return useContext(PrintApiContext);
}

interface MasterDocumentPrintProviderProps {
  printing?: MasterDocumentPrintConfig;
  /** Etiqueta del documento (template.identity.label) para el título por defecto. */
  documentLabel?: string;
  children: ReactNode;
}

/**
 * Host de impresión: posee el estado `{ open, ctx }`, monta UN único
 * `MasterDocumentPrintModal`, resuelve la config (`reportCode`/`apiBaseUrl`/
 * `parameters`/…) a partir de `printing` + el `PrintContext`, y expone la API
 * imperativa por contexto. Cuando no se pasa `printing`, el contexto queda en
 * `null` y todo el subsistema de impresión queda inerte.
 */
export function MasterDocumentPrintProvider({
  printing,
  documentLabel,
  children,
}: MasterDocumentPrintProviderProps) {
  const { t } = useMasterDocumentTranslation();
  const [state, setState] = useState<{ open: boolean; ctx: PrintContext | null }>({
    open: false,
    ctx: null,
  });

  // Regla (a): la acción se MUESTRA si está habilitada, el documento está
  // guardado (tiene id) y `canPrint` no la restringe.
  const isPrintAvailable = useCallback(
    (ctx: PrintContext): boolean => {
      if (!printing) return false;
      if (printing.enabled === false) return false;
      if (!ctx.documentId) return false;
      return printing.canPrint?.(ctx) ?? true;
    },
    [printing],
  );

  const openPrint = useCallback(
    (ctx: PrintContext) => {
      if (!printing) return;
      setState({ open: true, ctx });
    },
    [printing],
  );

  const close = useCallback(() => setState({ open: false, ctx: null }), []);

  const api = useMemo<MasterDocumentPrintApi | null>(() => {
    if (!printing) return null;
    return {
      openPrint,
      isPrintAvailable,
      label: printing.label ?? t('master_document.document.print'),
      buttonVariant: printing.buttonVariant ?? 'ghost',
    };
  }, [printing, openPrint, isPrintAvailable, t]);

  // Resuelve los datos del modal a partir de la config + el ctx activo.
  const resolved = useMemo(() => {
    if (!printing || !state.ctx) return null;
    const ctx = state.ctx;
    const reportId =
      typeof printing.reportCode === 'function' ? printing.reportCode(ctx) : printing.reportCode;
    const apiBaseUrl =
      typeof printing.apiBaseUrl === 'function' ? printing.apiBaseUrl() : printing.apiBaseUrl;
    const parameters = printing.buildParameters?.(ctx) ?? { id: ctx.documentId };

    // Regla (b): backend configurado ⇒ hay visor + apiBaseUrl + reportCode.
    const hasViewer = !!(printing.viewer || printing.renderViewer);
    const backendConfigured = hasViewer && !!apiBaseUrl && !!reportId;

    const defaultTitle = `Imprimir${documentLabel ? ` — ${documentLabel}` : ''}`;
    const title = printing.title?.(ctx) ?? defaultTitle;

    const viewerProps: PrintViewerProps = {
      reportId: reportId ?? '',
      parameters,
      apiBaseUrl: apiBaseUrl ?? '',
      tenantId: printing.tenantId,
      authToken: printing.authToken,
    };

    return { title, backendConfigured, viewerProps };
  }, [printing, state.ctx, documentLabel]);

  return (
    <PrintApiContext.Provider value={api}>
      {children}
      {printing && resolved && (
        <MasterDocumentPrintModal
          isOpen={state.open}
          title={resolved.title}
          onClose={close}
          backendConfigured={resolved.backendConfigured}
          notConfiguredMessage={printing.notConfiguredMessage}
          viewer={printing.viewer}
          renderViewer={printing.renderViewer}
          viewerProps={resolved.viewerProps}
        />
      )}
    </PrintApiContext.Provider>
  );
}
